import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_URL } from "../config/api";

const BACKEND_API_URL = API_URL;

export interface User {
  id: string;
  name: string;
  avatar: string;
  email: string;
}

interface AdministrationState {
  users: User[];
  loading: boolean;
  error: string | null;
}

const initialState: AdministrationState = {
  users: [],
  loading: false,
  error: null,
};

export const fetchUsers = createAsyncThunk(
  "administration/fetchUsers",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: { token: string | null; user: { companyId?: string; id?: string } | null } };
      const token = state.auth.token;
      const companyId = state.auth.user?.companyId || state.auth.user?.id;

      if (!token) {
        return rejectWithValue("No authentication token found");
      }

      if (!companyId) {
        return rejectWithValue("No company ID found");
      }

      const response = await axios.get(
        `${BACKEND_API_URL}/company/listusers/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Backend returns List<string> (user IDs)
      // Map to User interface with minimal info
      // TODO: Backend should return full user objects or add endpoint to fetch user details
      const userIds: string[] = response.data;
      return userIds.map((id) => ({
        id,
        name: id, // Temporary: using ID as name until we fetch user details
        avatar: "",
        email: "",
      })) as User[];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch users"
      );
    }
  }
);

const administrationSlice = createSlice({
  name: "administration",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<User[]>) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Failed to fetch users";
      });
  },
});

export const { clearError } = administrationSlice.actions;
export default administrationSlice.reducer;
